<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { formatPrice } from '@/utils/formatPrice';
import { getDateFormat, getDateHourMinute } from '@/utils/formatDate';

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();

// ── Commerces ─────────────────────────────────────────────────────────────────
const selectedRestaurantId = ref<string | null>(null);
const searchCommerce = ref('');

const { data: commercesData } = useQuery({
  queryKey: ['cmd-commerces'],
  queryFn: async () => {
    const { data } = await axiosInstance.get('restaurants/search', {
      params: { pagination: true, pageSize: 100, sortBy: 'name', order: 'ASC' },
    });
    return data;
  },
});

const allCommerces = computed<any[]>(() => {
  const list =
    commercesData.value?.data ?? commercesData.value?.restaurants ?? [];
  return Array.isArray(list) ? list : [];
});

const filteredCommerces = computed(() => {
  const q = searchCommerce.value.trim().toLowerCase();
  if (!q) return allCommerces.value;
  return allCommerces.value.filter((c: any) =>
    (c.designation ?? c.name ?? '').toLowerCase().includes(q),
  );
});

function storeInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const selectedCommerceName = computed(() => {
  if (!selectedRestaurantId.value) return null;
  const c = allCommerces.value.find(
    (c: any) => c.id === selectedRestaurantId.value,
  );
  return c ? (c.designation ?? c.name ?? '') : null;
});

// ── Orders ────────────────────────────────────────────────────────────────────
const currentPage = ref(1);
const PAGE_SIZE = 12;
const filterStatus = ref('');
const filterPayed = ref('');
const filterChannel = ref('');
const filterServiceMode = ref('');
const filterSort = ref('newer');

watch(
  [
    filterStatus,
    filterPayed,
    filterChannel,
    filterServiceMode,
    filterSort,
    selectedRestaurantId,
  ],
  () => {
    currentPage.value = 1;
  },
);

const SORT_OPTIONS = computed(() => [
  { value: 'newer', label: t('orders.sortNewer') },
  { value: 'older', label: t('orders.sortOlder') },
  { value: 'price', label: t('orders.sortPriceAsc') },
  { value: 'price_d', label: t('orders.sortPriceDesc') },
]);
const PAYED_OPTIONS = computed(() => [
  { value: 'paid', label: t('orders.paid') },
  { value: 'payment_pending', label: t('orders.paymentPending') },
  { value: 'refunded', label: t('orders.refunded') },
]);
const CHANNEL_OPTIONS = computed(() => [
  { value: 'click_and_collect', label: t('orders.channelClickAndCollect') },
  { value: 'pos_register', label: t('orders.channelPosRegister') },
]);
const SERVICE_MODE_OPTIONS = computed(() => [
  { value: 'to_prepare', label: t('orders.serviceModeToPrepare') },
  { value: 'direct_service', label: t('orders.serviceModeDirect') },
]);
const STATUS_MAP = computed<Record<string, { label: string; cls: string }>>(
  () => ({
    pending: { label: t('orders.statusPending'), cls: 'badge--amber' },
    in_progress: { label: t('orders.statusInProgress'), cls: 'badge--blue' },
    ready: { label: t('orders.statusReady'), cls: 'badge--green' },
    served: { label: t('orders.statusServed'), cls: 'badge--green' },
    completed: { label: t('orders.statusCompleted'), cls: 'badge--gray' },
    terminated: { label: t('orders.statusTerminated'), cls: 'badge--gray' },
    cancelled: { label: t('orders.statusCancelled'), cls: 'badge--red' },
    rejected: { label: t('orders.statusRejected'), cls: 'badge--red' },
    failled: { label: t('orders.statusFailed'), cls: 'badge--red' },
  }),
);
const STATUSES = computed(() =>
  Object.entries(STATUS_MAP.value).map(([k, v]) => ({
    value: k,
    label: v.label,
  })),
);

const statusInfo = (s: string) =>
  STATUS_MAP.value[s] ?? { label: s, cls: 'badge--gray' };

const payLabel = (o: any): { text: string; cls: string } => {
  const s = o.paymentStatus ?? o.payed ?? '';
  if (s === 'paid' || s === 'totaly_paid')
    return { text: t('orders.paid'), cls: 'pay--green' };
  if (s === 'payment_pending' || s === 'pending')
    return { text: t('orders.paymentAtt'), cls: 'pay--amber' };
  if (s === 'refunded') return { text: t('orders.refunded'), cls: 'pay--blue' };
  return { text: s || '—', cls: 'pay--gray' };
};

const pickupLabel = (o: any) => {
  if (o.pickupMethod === 'takeaway')
    return { text: t('orders.takeaway'), icon: 'shopping_bag' };
  if (o.pickupMethod === 'dine_in' || o.pickupMethod === 'onsite')
    return { text: t('orders.onsite'), icon: 'restaurant' };
  if (o.pickupMethod === 'delivery')
    return { text: t('orders.delivery'), icon: 'local_shipping' };
  return { text: o.pickupMethod ?? '', icon: 'receipt' };
};

const clientName = (o: any) => {
  const u = o.user;
  if (u?.firstname || u?.lastname)
    return `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim();
  if (o.phone) return o.phone;
  return t('orders.anonymous');
};

const itemsSummary = (items: any[]) => {
  if (!items?.length) return '—';
  return (
    items
      .slice(0, 3)
      .map((i: any) => `${i.qty}× ${i.product?.designation ?? '?'}`)
      .join(', ') + (items.length > 3 ? `, +${items.length - 3}` : '')
  );
};

const chosenOptions = (extra: any): string => {
  if (!extra?.options?.length) return '';
  return extra.options
    .map(
      (o: any) => `${o.qty > 1 ? o.qty + '× ' : ''}${o.option?.content ?? '?'}`,
    )
    .join(', ');
};

// ── Query orders ──────────────────────────────────────────────────────────────
const ordersKey = computed(() => [
  'cmd-orders',
  selectedRestaurantId.value,
  currentPage.value,
  filterStatus.value,
  filterPayed.value,
  filterChannel.value,
  filterServiceMode.value,
  filterSort.value,
]);

const {
  data: ordersData,
  isLoading,
  isError,
  refetch,
} = useQuery({
  queryKey: ordersKey,
  queryFn: async () => {
    const p = new URLSearchParams({
      pagination: 'true',
      page: String(currentPage.value),
      pageSize: String(PAGE_SIZE),
      sort: filterSort.value,
    });
    if (selectedRestaurantId.value)
      p.append('restaurantId', selectedRestaurantId.value);
    if (filterStatus.value) p.append('status', filterStatus.value);
    if (filterPayed.value) p.append('payed', filterPayed.value);
    // channel/serviceMode sont des filtres array côté serveur — même
    // convention que storeId/productId dans store-products.services.ts
    // (clé répétée), une seule valeur sélectionnable ici pour rester
    // cohérent avec les autres filtres à choix unique de cette page.
    if (filterChannel.value) p.append('channel', filterChannel.value);
    if (filterServiceMode.value)
      p.append('serviceMode', filterServiceMode.value);
    return ((await axiosInstance.get(`orders/search?${p}`)) as any).data;
  },
});

const orders = computed<any[]>(() => (ordersData.value as any)?.data ?? []);
const total = computed(() => (ordersData.value as any)?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / PAGE_SIZE)),
);

const visiblePages = computed(() => {
  const t = totalPages.value,
    c = currentPage.value;
  if (t <= 5) return Array.from({ length: t }, (_, i) => i + 1);
  if (c <= 3) return [1, 2, 3, '...', t];
  if (c >= t - 2) return [1, '...', t - 2, t - 1, t];
  return [1, '...', c - 1, c, c + 1, '...', t];
});

// ── Navigation détail ─────────────────────────────────────────────────────────
function openDetail(o: any) {
  router.push(`/commandes/${o.id}`);
}
</script>

<template>
  <div class="cmd">
    <!-- ══ Sub-sidebar commerces ══════════════════════════════════════════════ -->
    <aside class="cmd__sidebar">
      <h2 class="cmd__sidebar-title">
        {{ t('orders.commerces') }}
        <span class="cmd__sidebar-count">{{ allCommerces.length }}</span>
      </h2>

      <div class="cmd__sidebar-search">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="cmd__sidebar-search-icon"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          v-model="searchCommerce"
          :placeholder="t('orders.filter')"
          class="cmd__sidebar-input"
        />
      </div>

      <div class="cmd__sidebar-list">
        <button
          v-for="c in filteredCommerces"
          :key="c.id"
          class="cmd__store-btn"
          :class="{ 'cmd__store-btn--active': selectedRestaurantId === c.id }"
          @click="
            selectedRestaurantId = selectedRestaurantId === c.id ? null : c.id
          "
        >
          <div
            class="cmd__store-avatar"
            :class="
              selectedRestaurantId === c.id ? 'cmd__store-avatar--active' : ''
            "
          >
            {{ storeInitials(c.designation ?? c.name ?? '?') }}
          </div>
          <span class="cmd__store-name">{{ c.designation ?? c.name }}</span>
        </button>

        <p v-if="filteredCommerces.length === 0" class="cmd__sidebar-empty">
          {{ t('orders.noResult') }}
        </p>
      </div>
    </aside>

    <!-- ══ Main content ═══════════════════════════════════════════════════════ -->
    <section class="cmd__main">
      <!-- Header + filters -->
      <div class="cmd__topbar">
        <div>
          <div class="cmd__topbar-title-row">
            <h2 class="cmd__title">{{ t('orders.title') }}</h2>
            <span v-if="!isLoading" class="cmd__total-badge">{{ total }}</span>
          </div>
          <p class="cmd__subtitle">
            {{
              selectedCommerceName
                ? t('orders.subtitleFor', { name: selectedCommerceName })
                : t('orders.subtitle')
            }}
          </p>
        </div>

        <div class="cmd__filters">
          <!-- Sort -->
          <div class="cmd__filter-pill">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <select v-model="filterSort" class="cmd__filter-select">
              <option v-for="s in SORT_OPTIONS" :key="s.value" :value="s.value">
                {{ s.label }}
              </option>
            </select>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <!-- Status -->
          <div class="cmd__filter-pill">
            <select v-model="filterStatus" class="cmd__filter-select">
              <option value="">{{ t('orders.allStatuses') }}</option>
              <option v-for="s in STATUSES" :key="s.value" :value="s.value">
                {{ s.label }}
              </option>
            </select>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <!-- Payment -->
          <div class="cmd__filter-pill">
            <select v-model="filterPayed" class="cmd__filter-select">
              <option value="">{{ t('orders.allPayments') }}</option>
              <option
                v-for="p in PAYED_OPTIONS"
                :key="p.value"
                :value="p.value"
              >
                {{ p.label }}
              </option>
            </select>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <!-- Channel -->
          <div class="cmd__filter-pill">
            <select v-model="filterChannel" class="cmd__filter-select">
              <option value="">{{ t('orders.allChannels') }}</option>
              <option
                v-for="c in CHANNEL_OPTIONS"
                :key="c.value"
                :value="c.value"
              >
                {{ c.label }}
              </option>
            </select>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <!-- Service mode -->
          <div class="cmd__filter-pill">
            <select v-model="filterServiceMode" class="cmd__filter-select">
              <option value="">{{ t('orders.allServiceModes') }}</option>
              <option
                v-for="sm in SERVICE_MODE_OPTIONS"
                :key="sm.value"
                :value="sm.value"
              >
                {{ sm.label }}
              </option>
            </select>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <!-- Refresh -->
          <button class="cmd__refresh-btn" @click="refetch()">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Loading skeleton -->
      <div v-if="isLoading" class="cmd__grid">
        <div v-for="i in 6" :key="i" class="cmd__skeleton">
          <div class="cmd__skeleton-head">
            <div class="cmd__sk-block" style="width: 55%; height: 1.1rem" />
            <div
              class="cmd__sk-block"
              style="width: 5rem; height: 1.5rem; border-radius: 9999px"
            />
          </div>
          <div class="cmd__skeleton-mid">
            <div
              class="cmd__sk-block"
              style="width: 2.5rem; height: 2.5rem; border-radius: 50%"
            />
            <div
              style="
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 0.4rem;
              "
            >
              <div class="cmd__sk-block" style="width: 60%; height: 0.875rem" />
              <div class="cmd__sk-block" style="width: 40%; height: 0.75rem" />
            </div>
          </div>
          <div class="cmd__sk-block" style="width: 80%; height: 0.75rem" />
          <div class="cmd__skeleton-foot">
            <div class="cmd__sk-block" style="width: 5rem; height: 1.75rem" />
            <div
              class="cmd__sk-block"
              style="width: 4rem; height: 1.25rem; border-radius: 9999px"
            />
          </div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="isError" class="cmd__empty">
        <div class="cmd__empty-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p class="cmd__empty-title">{{ t('orders.loadError') }}</p>
        <button class="cmd__retry-btn" @click="refetch()">
          {{ t('common.retry') }}
        </button>
      </div>

      <!-- Empty -->
      <div v-else-if="orders.length === 0" class="cmd__empty">
        <div class="cmd__empty-icon">
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.2"
          >
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
        </div>
        <p class="cmd__empty-title">
          {{
            selectedCommerceName
              ? t('orders.noOrderFor', { name: selectedCommerceName })
              : t('orders.noOrder')
          }}
        </p>
      </div>

      <!-- Bento grid -->
      <div v-else class="cmd__grid">
        <div
          v-for="order in orders"
          :key="order.id"
          class="cmd__card"
          @click="openDetail(order)"
        >
          <!-- Top row: ID + badge -->
          <div class="cmd__card-head">
            <div>
              <span class="cmd__card-id-label">{{ t('orders.orderId') }}</span>
              <h3 class="cmd__card-id">{{ order.orderNumber }}</h3>
              <p class="cmd__card-date">
                {{ getDateFormat(order.createdAt) }},
                {{ getDateHourMinute(order.createdAt) }}
              </p>
            </div>
            <span class="cmd__badge" :class="statusInfo(order.status).cls">
              {{ statusInfo(order.status).label }}
            </span>
          </div>

          <!-- Client + type -->
          <div class="cmd__card-mid">
            <div class="cmd__card-client-row">
              <div class="cmd__card-client-avatar">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div>
                <p class="cmd__card-label">
                  {{ t('orders.client').toUpperCase() }}
                </p>
                <p class="cmd__card-client-name">{{ clientName(order) }}</p>
              </div>
            </div>
            <div class="cmd__card-type">
              <p class="cmd__card-label">
                {{ t('orders.type').toUpperCase() }}
              </p>
              <div class="cmd__card-type-val">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#141c29"
                  stroke-width="2"
                >
                  <path
                    v-if="pickupLabel(order).icon === 'shopping_bag'"
                    d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"
                  />
                  <path
                    v-else-if="pickupLabel(order).icon === 'local_shipping'"
                    d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3m0 0h4l3 3v4h-7m0 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm7 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"
                  />
                  <path
                    v-else
                    d="M3 2h1l.4 2M7 13h10l4-8H5.4M7 13L5.4 4M7 13l-1.4 5h11.8"
                  />
                </svg>
                <span>{{ pickupLabel(order).text }}</span>
              </div>
            </div>
          </div>

          <!-- Articles -->
          <div class="cmd__card-items">
            <p class="cmd__card-label">{{ t('orders.items').toUpperCase() }}</p>
            <p class="cmd__card-items-text">{{ itemsSummary(order.items) }}</p>
          </div>

          <!-- Footer: price + payment -->
          <div class="cmd__card-foot">
            <div>
              <p class="cmd__card-label">
                {{ t('orders.total').toUpperCase() }}
              </p>
              <p class="cmd__card-price">{{ formatPrice(order.totalPrice) }}</p>
            </div>
            <span class="cmd__pay-chip" :class="payLabel(order).cls">
              <svg
                v-if="payLabel(order).cls === 'pay--green'"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <svg
                v-else
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="1" y="4" width="22" height="16" rx="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
              {{ payLabel(order).text }}
            </span>
          </div>

          <!-- Hover actions -->
          <div class="cmd__card-actions">
            <button
              class="cmd__card-btn cmd__card-btn--ghost"
              @click.stop="openDetail(order)"
            >
              {{ t('orders.details') }}
            </button>
            <button
              class="cmd__card-btn cmd__card-btn--primary"
              @click.stop="openDetail(order)"
            >
              {{ t('orders.view') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="cmd__pagination">
        <span class="cmd__pagination-info">
          {{ (currentPage - 1) * PAGE_SIZE + 1 }}–{{
            Math.min(currentPage * PAGE_SIZE, total)
          }}
          {{ t('orders.paginationOf') }} {{ total }}
        </span>
        <div class="cmd__pagination-btns">
          <button
            :disabled="currentPage === 1"
            class="cmd__page-btn"
            @click="currentPage--"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <template v-for="(p, i) in visiblePages" :key="i">
            <span v-if="p === '...'" class="cmd__page-dots">…</span>
            <button
              v-else
              class="cmd__page-btn"
              :class="{ 'cmd__page-btn--active': currentPage === p }"
              @click="currentPage = p as number"
            >
              {{ p }}
            </button>
          </template>
          <button
            :disabled="currentPage === totalPages"
            class="cmd__page-btn"
            @click="currentPage++"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* ══ Layout ══════════════════════════════════════════════════════════════════ */
.cmd {
  display: flex;
  gap: 0;
  align-items: flex-start;
  min-height: calc(100vh - 5rem);
}

/* ══ Sub-sidebar ══════════════════════════════════════════════════════════════ */
.cmd__sidebar {
  flex-shrink: 0;
  width: 17rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 1.5rem;
  position: sticky;
  top: 5rem;
  height: calc(100vh - 5rem);
  overflow: hidden;
}

.cmd__sidebar-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #141c29;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cmd__sidebar-count {
  font-size: 0.75rem;
  background: #e7eeff;
  color: #4d4632;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-weight: 600;
}

.cmd__sidebar-search {
  position: relative;
  display: flex;
  align-items: center;
}
.cmd__sidebar-search-icon {
  position: absolute;
  left: 0.625rem;
  color: #4d4632;
  opacity: 0.5;
  pointer-events: none;
}
.cmd__sidebar-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 0.75rem;
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  font-size: 0.875rem;
  font-family: inherit;
  color: #141c29;
  outline: none;
  transition: background 0.15s;
}
.cmd__sidebar-input:focus {
  background: rgba(255, 255, 255, 0.8);
}
.cmd__sidebar-input::placeholder {
  color: #4d4632;
  opacity: 0.5;
}

.cmd__sidebar-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
  flex: 1;
  scrollbar-width: none;
}
.cmd__sidebar-list::-webkit-scrollbar {
  display: none;
}

.cmd__sidebar-empty {
  font-size: 0.8125rem;
  color: #94a3b8;
  text-align: center;
  padding: 1rem 0;
  margin: 0;
}

.cmd__store-btn {
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  border: 1px solid transparent;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  cursor: pointer;
  transition: background 0.15s;
}
.cmd__store-btn:hover {
  background: rgba(255, 255, 255, 0.4);
}
.cmd__store-btn--active {
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.6);
  box-shadow: 0 2px 8px rgba(11, 19, 32, 0.04);
}

.cmd__store-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #e7eeff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #4d4632;
  opacity: 0.6;
  flex-shrink: 0;
}
.cmd__store-avatar--active {
  background: #0b1320;
  color: #ffd60a;
  opacity: 1;
}
.cmd__store-btn--active .cmd__store-avatar {
  opacity: 1;
}

.cmd__store-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4d4632;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cmd__store-btn--active .cmd__store-name {
  color: #141c29;
  font-weight: 700;
}

/* ══ Main ════════════════════════════════════════════════════════════════════ */
.cmd__main {
  flex: 1;
  min-width: 0;
  padding: 2rem;
  background: rgba(240, 243, 255, 0.3);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: calc(100vh - 5rem);
}

/* Topbar */
.cmd__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.cmd__topbar-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}
.cmd__title {
  font-size: 2rem;
  font-weight: 700;
  color: #141c29;
  margin: 0;
  letter-spacing: -0.01em;
}
.cmd__total-badge {
  background: #0b1320;
  color: #ffd60a;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 0.15rem 0.75rem;
  border-radius: 9999px;
}
.cmd__subtitle {
  font-size: 0.875rem;
  color: #4d4632;
  opacity: 0.7;
  margin: 0;
}

.cmd__filters {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}

.cmd__filter-pill {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: background 0.15s;
}
.cmd__filter-pill:hover {
  background: rgba(255, 255, 255, 1);
}
.cmd__filter-select {
  border: none;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: inherit;
  color: #141c29;
  outline: none;
  cursor: pointer;
  max-width: 140px;
}
.cmd__refresh-btn {
  width: 2.625rem;
  height: 2.625rem;
  background: #0b1320;
  color: #ffd60a;
  border: none;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.15s;
}
.cmd__refresh-btn:hover {
  opacity: 0.85;
}

/* ══ Grid ════════════════════════════════════════════════════════════════════ */
.cmd__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

/* Skeleton */
.cmd__skeleton {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: sk-pulse 1.5s ease-in-out infinite;
}
.cmd__skeleton-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.cmd__skeleton-mid {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.cmd__skeleton-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.5rem;
}
.cmd__sk-block {
  background: #e7eeff;
  border-radius: 0.5rem;
}
@keyframes sk-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

/* Empty */
.cmd__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 5rem;
  text-align: center;
}
.cmd__empty-icon {
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  background: #e7eeff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4d4632;
  opacity: 0.5;
}
.cmd__empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #141c29;
  margin: 0;
}
.cmd__retry-btn {
  padding: 0.5rem 1.5rem;
  border-radius: 0.75rem;
  background: #ffd60a;
  color: #0b1320;
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

/* ══ Order Card ══════════════════════════════════════════════════════════════ */
.cmd__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  padding: 1.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  cursor: pointer;
  transition:
    box-shadow 0.5s cubic-bezier(0.2, 1, 0.3, 1),
    transform 0.5s cubic-bezier(0.2, 1, 0.3, 1);
}
.cmd__card:hover {
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.cmd__card--selected {
  box-shadow:
    0 0 0 2px #141c29,
    0 20px 48px rgba(112, 93, 0, 0.12);
}

/* Card sub-sections */
.cmd__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}
.cmd__card-id-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #4d4632;
  opacity: 0.6;
}
.cmd__card-id {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #141c29;
  margin: 0.125rem 0 0;
}
.cmd__card-date {
  font-size: 0.6875rem;
  color: #4d4632;
  opacity: 0.6;
  margin-top: 0.25rem;
}
.cmd__card-label {
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #4d4632;
  opacity: 0.6;
  display: block;
  margin-bottom: 0.2rem;
}

.cmd__card-mid {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}
.cmd__card-client-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.cmd__card-client-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #e7eeff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4d4632;
  flex-shrink: 0;
}
.cmd__card-client-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #141c29;
}
.cmd__card-type {
  text-align: right;
}
.cmd__card-type-val {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  justify-content: flex-end;
  font-size: 0.875rem;
  font-weight: 600;
  color: #141c29;
}

.cmd__card-items-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #141c29;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cmd__card-foot {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}
.cmd__card-price {
  font-size: 1.5rem;
  font-weight: 800;
  color: #141c29;
  margin-top: 0.1rem;
}

/* Hover actions */
.cmd__card-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  opacity: 0;
  transition: opacity 0.3s;
}
.cmd__card:hover .cmd__card-actions {
  opacity: 1;
}
.cmd__card-btn {
  padding: 0.75rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
}
.cmd__card-btn--ghost {
  background: #f0f3ff;
  color: #141c29;
  border: 1px solid rgba(255, 255, 255, 0.6);
}
.cmd__card-btn--ghost:hover {
  background: #fff;
}
.cmd__card-btn--primary {
  background: #ffd60a;
  color: #0b1320;
  border: none;
}
.cmd__card-btn--primary:hover {
  background: #e6c109;
}

/* ══ Status badge ════════════════════════════════════════════════════════════ */
.cmd__badge {
  flex-shrink: 0;
  padding: 0.2rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  border: 1px solid transparent;
}
.badge--amber {
  background: #fef3c7;
  color: #b45309;
  border-color: #fde68a;
}
.badge--blue {
  background: #dbeafe;
  color: #1d4ed8;
  border-color: #bfdbfe;
}
.badge--green {
  background: #dcfce7;
  color: #15803d;
  border-color: #bbf7d0;
}
.badge--gray {
  background: #f1f5f9;
  color: #475569;
  border-color: #e2e8f0;
}
.badge--red {
  background: #fee2e2;
  color: #dc2626;
  border-color: #fecaca;
}

/* ══ Pay chip ════════════════════════════════════════════════════════════════ */
.cmd__pay-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.625rem;
  border-radius: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
}
.pay--green {
  background: #dcfce7;
  color: #15803d;
}
.pay--amber {
  background: #fef3c7;
  color: #b45309;
}
.pay--blue {
  background: #dbeafe;
  color: #1d4ed8;
}
.pay--gray {
  background: #f1f5f9;
  color: #475569;
}

/* ══ Pagination ══════════════════════════════════════════════════════════════ */
.cmd__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}
.cmd__pagination-info {
  font-size: 0.8125rem;
  color: #4d4632;
  opacity: 0.7;
}
.cmd__pagination-btns {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.cmd__page-btn {
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 0.375rem;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.6);
  font-size: 0.8125rem;
  font-weight: 500;
  color: #4d4632;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;
}
.cmd__page-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 1);
}
.cmd__page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.cmd__page-btn--active {
  background: #0b1320;
  color: #fff;
  border-color: #0b1320;
  font-weight: 700;
}
.cmd__page-dots {
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

/* ══ Detail panel ════════════════════════════════════════════════════════════ */
.cmd-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(11, 19, 32, 0.25);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: flex-end;
}
.cmd-detail {
  width: min(520px, 96vw);
  height: 100%;
  background: rgba(249, 249, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-left: 1px solid rgba(255, 255, 255, 0.4);
  display: flex;
  flex-direction: column;
  box-shadow: -12px 0 48px rgba(0, 0, 0, 0.08);
}
.cmd-detail__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}
.cmd-detail__id {
  font-size: 1.125rem;
  font-weight: 700;
  color: #141c29;
  margin: 0.2rem 0 0;
}
.cmd-detail__close {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4d4632;
  flex-shrink: 0;
  transition: background 0.15s;
}
.cmd-detail__close:hover {
  background: #fff;
}

.cmd-detail__body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  scrollbar-width: thin;
  scrollbar-color: #e7eeff transparent;
}

.cmd-detail__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
}
.cmd-detail__cell {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 0.875rem;
  padding: 0.75rem 1rem;
}
.cmd-detail__cell-val {
  font-size: 0.875rem;
  font-weight: 600;
  color: #141c29;
}

.cmd-detail__reject {
  background: rgba(186, 26, 26, 0.06);
  border: 1px solid rgba(186, 26, 26, 0.2);
  border-radius: 0.875rem;
  padding: 0.875rem 1rem;
}
.cmd-detail__reject-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #dc2626;
  margin: 0 0 0.25rem;
}
.cmd-detail__reject-msg {
  font-size: 0.875rem;
  color: #991b1b;
  margin: 0;
}

.cmd-detail__section {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cmd-detail__section-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #141c29;
  margin: 0;
}

.cmd-detail__client {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 0.875rem;
}
.cmd-detail__client-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #e7eeff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4d4632;
  flex-shrink: 0;
}
.cmd-detail__client-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #141c29;
  margin: 0;
}
.cmd-detail__client-sub {
  font-size: 0.8125rem;
  color: #4d4632;
  margin: 0.1rem 0 0;
  opacity: 0.8;
}

.cmd-detail__pay-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 0.875rem;
}
.cmd-detail__pay-meta {
  font-size: 0.8125rem;
  color: #4d4632;
}
.cmd-detail__pay-cur {
  font-size: 0.75rem;
  color: #94a3b8;
  margin: 0.25rem 0 0;
}
.cmd-detail__pay-amount {
  font-size: 1rem;
  font-weight: 700;
  color: #141c29;
}

.cmd-detail__totals {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cmd-detail__total-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #4d4632;
}
.cmd-detail__total-row span:last-child {
  font-weight: 600;
  color: #141c29;
}
.cmd-detail__total-row--final {
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}
.cmd-detail__total-row--final span {
  font-size: 1rem;
  font-weight: 800;
  color: #141c29;
}

.cmd-detail__note {
  background: rgba(255, 251, 235, 0.8);
  border: 1px solid #fde68a;
  border-radius: 0.875rem;
  padding: 0.875rem 1rem;
}
.cmd-detail__note-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #b45309;
  margin: 0 0 0.25rem;
}
.cmd-detail__note-text {
  font-size: 0.875rem;
  color: #92400e;
  font-style: italic;
  margin: 0;
}

.cmd-detail__items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.cmd-item {
  display: flex;
  gap: 0.875rem;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 0.875rem;
  padding: 0.875rem;
}
.cmd-item__img {
  width: 3.5rem;
  height: 3.5rem;
  flex-shrink: 0;
  border-radius: 0.625rem;
  background: #e7eeff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cmd-item__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cmd-item__body {
  flex: 1;
  min-width: 0;
}
.cmd-item__name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #141c29;
  margin: 0;
}
.cmd-item__cat {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin: 0.125rem 0 0;
}
.cmd-item__qty {
  font-size: 0.8125rem;
  color: #4d4632;
  margin: 0.2rem 0 0;
}
.cmd-item__note {
  font-size: 0.6875rem;
  color: #4d4632;
  font-style: italic;
  margin: 0.25rem 0 0;
}
.cmd-item__total {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #141c29;
  flex-shrink: 0;
}

.cmd-item__extras {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}
.cmd-item__extra-title {
  font-size: 0.625rem;
  font-weight: 700;
  color: #4d4632;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
}
.cmd-item__extra-opts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.cmd-item__opt {
  font-size: 0.6875rem;
  color: #141c29;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 0.375rem;
  padding: 0.15rem 0.5rem;
}
.cmd-item__opt-price {
  color: #141c29;
  font-weight: 700;
}

/* ══ Panel transition ════════════════════════════════════════════════════════ */
.cmd-panel-enter-active {
  transition: opacity 0.25s ease;
}
.cmd-panel-leave-active {
  transition: opacity 0.2s ease;
}
.cmd-panel-enter-from,
.cmd-panel-leave-to {
  opacity: 0;
}
.cmd-panel-enter-active .cmd-detail {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.cmd-panel-leave-active .cmd-detail {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.cmd-panel-enter-from .cmd-detail {
  transform: translateX(100%);
}
.cmd-panel-leave-to .cmd-detail {
  transform: translateX(100%);
}

/* ══ Responsive ══════════════════════════════════════════════════════════════ */
@media (max-width: 900px) {
  .cmd {
    flex-direction: column;
  }
  .cmd__sidebar {
    width: 100%;
    height: auto;
    position: static;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  .cmd__sidebar-list {
    max-height: 180px;
  }
  .cmd__main {
    padding: 1.25rem;
  }
}

/* ══ Dark mode ═══════════════════════════════════════════════════════════════ */
[data-theme='dark'] .cmd__sidebar {
  background: rgba(20, 28, 41, 0.85);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd__sidebar-title {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd__sidebar-input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}
[data-theme='dark'] .cmd__store-btn:hover {
  background: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cmd__store-btn--active {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd__store-name {
  color: #94a3b8;
}
[data-theme='dark'] .cmd__store-btn--active .cmd__store-name {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd__main {
  background: transparent;
}
[data-theme='dark'] .cmd__title {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd__filter-pill {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd__filter-select {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd__card:hover {
  border-color: rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .cmd__card-id,
[data-theme='dark'] .cmd__card-client-name,
[data-theme='dark'] .cmd__card-type-val,
[data-theme='dark'] .cmd__card-items-text {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd-detail {
  background: rgba(12, 20, 33, 0.98);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd-detail__head {
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd-detail__id,
[data-theme='dark'] .cmd-detail__client-name,
[data-theme='dark'] .cmd-detail__pay-amount,
[data-theme='dark'] .cmd-item__name,
[data-theme='dark'] .cmd-item__total,
[data-theme='dark'] .cmd-detail__section-title {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd-detail__cell,
[data-theme='dark'] .cmd-detail__client,
[data-theme='dark'] .cmd-detail__pay-row,
[data-theme='dark'] .cmd-detail__totals,
[data-theme='dark'] .cmd-item {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd-detail__total-row--final span {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd__store-avatar {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cmd__store-avatar--active {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cmd__total-badge {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cmd__refresh-btn {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cmd__card-btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cmd__card-btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cmd__card-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cmd__page-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
[data-theme='dark'] .cmd__subtitle {
  color: rgba(255, 255, 255, 0.55);
}
[data-theme='dark'] .cmd__card-id-label,
[data-theme='dark'] .cmd__card-date,
[data-theme='dark'] .cmd__card-label {
  color: rgba(255, 255, 255, 0.45);
  opacity: 1;
}
[data-theme='dark'] .cmd__card-price {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd__card-mid {
  border-top-color: rgba(255, 255, 255, 0.08);
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd__card-foot {
  border-top-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd__card-client-avatar {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cmd-detail__cell-val {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd-detail__client-sub,
[data-theme='dark'] .cmd-detail__pay-meta,
[data-theme='dark'] .cmd-detail__pay-cur {
  color: rgba(255, 255, 255, 0.5);
  opacity: 1;
}
[data-theme='dark'] .cmd-detail__total-row {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .cmd-detail__total-row span:last-child {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd-detail__total-row--final {
  border-top-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd-detail__close {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .cmd-detail__close:hover {
  background: rgba(255, 255, 255, 0.14);
}
[data-theme='dark'] .cmd-detail__client-avatar {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cmd-detail__note {
  background: rgba(251, 191, 36, 0.08);
  border-color: rgba(251, 191, 36, 0.2);
}
[data-theme='dark'] .cmd-detail__note-label {
  color: #fbbf24;
}
[data-theme='dark'] .cmd-detail__note-text {
  color: #fde68a;
}
[data-theme='dark'] .cmd-detail__reject {
  background: rgba(220, 38, 38, 0.08);
  border-color: rgba(220, 38, 38, 0.2);
}
[data-theme='dark'] .cmd-detail__reject-msg {
  color: #fca5a5;
}
[data-theme='dark'] .cmd__sidebar-count {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .cmd__sidebar-empty {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .cmd__filter-select option {
  background: #111c2e;
}
[data-theme='dark'] .cmd__sk-block {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd__empty-icon {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .cmd__empty-title {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd__retry-btn {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cmd__pagination {
  border-top-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd__pagination-info {
  color: rgba(255, 255, 255, 0.5);
  opacity: 1;
}
[data-theme='dark'] .cmd__page-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .cmd__page-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cmd__page-dots {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .cmd-item__img {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cmd-item__cat {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .cmd-item__qty {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .cmd-item__note {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .cmd-item__extra-title {
  color: rgba(255, 255, 255, 0.45);
  opacity: 1;
}
[data-theme='dark'] .cmd-item__opt {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .cmd-item__opt-price {
  color: #f1f5f9;
}
[data-theme='dark'] .cmd-detail__body {
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}
</style>
